/**
 * 学習ログ状態機械の localStorage 読み書き（08_client-state-machine.md §2・§8.3）。
 *
 * 11 キーはメモリ上では 1 つの LearningLogState として扱う。読み込み時は 1 つでも欠落・型不正なら
 * 全体を破棄する（部分復旧はしない。§2.4）。
 */
import type { LearningLogState, Phase } from "./state.js";

const KEY_PREFIX = "learningLog.";
const KEYS = {
  phase: `${KEY_PREFIX}phase`,
  hasSucceededToday: `${KEY_PREFIX}hasSucceededToday`,
  pendingCheckStartedAt: `${KEY_PREFIX}pendingCheckStartedAt`,
  pendingGetDone: `${KEY_PREFIX}pendingGetDone`,
  pendingHasLogToday: `${KEY_PREFIX}pendingHasLogToday`,
  toastShownAt: `${KEY_PREFIX}toastShownAt`,
  retryAt: `${KEY_PREFIX}retryAt`,
  silentUntil: `${KEY_PREFIX}silentUntil`,
  confirmRetryAt: `${KEY_PREFIX}confirmRetryAt`,
  lastSentAt: `${KEY_PREFIX}lastSentAt`,
  lastResetDate: `${KEY_PREFIX}lastResetDate`,
} as const;

const PHASES: readonly Phase[] = ["PENDING_CHECK", "AWAITING_CONFIRM", "CONFIRMED_SILENT"];

function parseJson(raw: string | null): unknown {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function isFiniteNonNegativeOrNull(value: unknown): value is number | null {
  if (value === null) return true;
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/** 11 キーを読み出して LearningLogState に組み立てる。1 つでも欠落・型不正なら null。 */
export function loadState(): LearningLogState | null {
  const phase = parseJson(localStorage.getItem(KEYS.phase));
  const hasSucceededToday = parseJson(localStorage.getItem(KEYS.hasSucceededToday));
  const pendingCheckStartedAt = parseJson(localStorage.getItem(KEYS.pendingCheckStartedAt));
  const pendingGetDone = parseJson(localStorage.getItem(KEYS.pendingGetDone));
  const pendingHasLogToday = parseJson(localStorage.getItem(KEYS.pendingHasLogToday));
  const toastShownAt = parseJson(localStorage.getItem(KEYS.toastShownAt));
  const retryAt = parseJson(localStorage.getItem(KEYS.retryAt));
  const silentUntil = parseJson(localStorage.getItem(KEYS.silentUntil));
  const confirmRetryAt = parseJson(localStorage.getItem(KEYS.confirmRetryAt));
  const lastSentAt = parseJson(localStorage.getItem(KEYS.lastSentAt));
  const lastResetDate = parseJson(localStorage.getItem(KEYS.lastResetDate));

  if (
    phase === undefined ||
    hasSucceededToday === undefined ||
    pendingCheckStartedAt === undefined ||
    pendingGetDone === undefined ||
    pendingHasLogToday === undefined ||
    toastShownAt === undefined ||
    retryAt === undefined ||
    silentUntil === undefined ||
    confirmRetryAt === undefined ||
    lastSentAt === undefined ||
    lastResetDate === undefined
  ) {
    return null;
  }

  if (typeof phase !== "string" || !PHASES.includes(phase as Phase)) return null;
  if (typeof hasSucceededToday !== "boolean") return null;
  if (typeof pendingGetDone !== "boolean") return null;
  if (typeof pendingHasLogToday !== "boolean" && pendingHasLogToday !== null) return null;
  if (!isFiniteNonNegativeOrNull(pendingCheckStartedAt)) return null;
  if (!isFiniteNonNegativeOrNull(toastShownAt)) return null;
  if (!isFiniteNonNegativeOrNull(retryAt)) return null;
  if (!isFiniteNonNegativeOrNull(silentUntil)) return null;
  if (!isFiniteNonNegativeOrNull(confirmRetryAt)) return null;
  if (!isFiniteNonNegativeOrNull(lastSentAt)) return null;
  if (typeof lastResetDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(lastResetDate)) return null;

  return {
    phase: phase as Phase,
    hasSucceededToday,
    pendingCheckStartedAt,
    pendingGetDone,
    pendingHasLogToday,
    toastShownAt,
    retryAt,
    silentUntil,
    confirmRetryAt,
    lastSentAt,
    lastResetDate,
  };
}

/** LearningLogState を 11 キーに分解して書き込む。書き込み失敗は握りつぶす。 */
export function saveState(state: LearningLogState): void {
  try {
    localStorage.setItem(KEYS.phase, JSON.stringify(state.phase));
    localStorage.setItem(KEYS.hasSucceededToday, JSON.stringify(state.hasSucceededToday));
    localStorage.setItem(KEYS.pendingCheckStartedAt, JSON.stringify(state.pendingCheckStartedAt));
    localStorage.setItem(KEYS.pendingGetDone, JSON.stringify(state.pendingGetDone));
    localStorage.setItem(KEYS.pendingHasLogToday, JSON.stringify(state.pendingHasLogToday));
    localStorage.setItem(KEYS.toastShownAt, JSON.stringify(state.toastShownAt));
    localStorage.setItem(KEYS.retryAt, JSON.stringify(state.retryAt));
    localStorage.setItem(KEYS.silentUntil, JSON.stringify(state.silentUntil));
    localStorage.setItem(KEYS.confirmRetryAt, JSON.stringify(state.confirmRetryAt));
    localStorage.setItem(KEYS.lastSentAt, JSON.stringify(state.lastSentAt));
    localStorage.setItem(KEYS.lastResetDate, JSON.stringify(state.lastResetDate));
  } catch {
    console.warn("learning-log: localStorage を利用できません。状態は保持されません");
  }
}
